import de from './de.json';
import en from './en.json';
import es from './es.json';
import fr from './fr.json';
import ja from './ja.json';
import ko from './ko.json';
import pt from './pt.json';

export const translations = {
  en,
  es,
  de,
  fr,
  ja,
  ko,
  pt,
} as const;

export type SupportedLocale = keyof typeof translations;
export type TranslationKey = keyof typeof en;
export type TranslationReplacements = Record<string, string | number>;

export interface TranslateOptions {
  locale?: string;
  replacements?: TranslationReplacements;
}

const DEFAULT_LOCALE: SupportedLocale = 'en';

function normalizeLocale(locale?: string): string | undefined {
  return locale?.toLowerCase().replace('_', '-');
}

export function resolveLocale(locale?: string): SupportedLocale {
  const normalized = normalizeLocale(locale);
  if (normalized && normalized in translations) {
    return normalized as SupportedLocale;
  }

  if (normalized) {
    const primary = normalized.split('-')[0];
    if (primary in translations) {
      return primary as SupportedLocale;
    }
  }

  return DEFAULT_LOCALE;
}

function formatTemplate(template: string, replacements?: TranslationReplacements): string {
  if (!replacements) {
    return template;
  }

  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key: string) => {
    const value = replacements[key];
    return value !== undefined && value !== null ? String(value) : '';
  });
}

export function translate(key: TranslationKey, options?: TranslateOptions): string {
  const locale = resolveLocale(options?.locale);
  const template = translations[locale][key] ?? translations[DEFAULT_LOCALE][key];
  if (!template) {
    return key;
  }

  return formatTemplate(template, options?.replacements);
}

export function getElementLocale(element?: HTMLElement | null): string | undefined {
  if (element?.getAttribute) {
    const attrLocale = element.getAttribute('lang');
    if (attrLocale) {
      return attrLocale;
    }
  }

  const ownerDocument =
    element?.ownerDocument ?? (typeof document !== 'undefined' ? document : undefined);
  const docLocale = ownerDocument?.documentElement?.getAttribute('lang');
  if (docLocale) {
    return docLocale;
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }

  return undefined;
}

export function translateForElement(
  element: HTMLElement | null,
  key: TranslationKey,
  replacements?: TranslationReplacements
): string {
  const locale = getElementLocale(element);
  return translate(key, { locale, replacements });
}
